/**
 * RequestContextPanel — the originating Catchball request, readable inside the
 * Event Designer.
 *
 * Finding F6.4 (docs/9-exploration/MOSSO-DEMO-FINDINGS-2026-08-25.md): the ball
 * arrives carrying a Request — strategic context, asks, constraints,
 * guardrails, what a good proposal answers — and the team then designed the
 * session with none of it on screen, from memory. The Designer showed only an
 * "Exchange: Request received" badge.
 *
 * Collapsed by default so it never competes with the step rail; one click and
 * the request is in front of the person designing the response to it.
 */

import { useState } from 'react';
import { ChevronDown, ChevronRight, Inbox } from 'lucide-react';
import type { LoopIntent } from '../../../types/prioritization/catchballLoop';

export interface RequestContextPanelProps {
  intent: LoopIntent | null;
  /** 'revision_received' reframes the panel — this is a returned proposal. */
  isRevision?: boolean;
  className?: string;
}

function Section({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <p className="text-[11px] font-semibold uppercase tracking-wide text-[var(--text-ter)] dark:text-[var(--text-ter)]">
        {label}
      </p>
      <div className="mt-1 text-sm text-[var(--text-sec)] dark:text-[var(--text-sec)]">{children}</div>
    </div>
  );
}

function BulletList({ items }: { items: string[] }) {
  return (
    <ul className="list-disc space-y-0.5 pl-4">
      {items.map((item, i) => (
        <li key={`${i}-${item.slice(0, 24)}`}>{item}</li>
      ))}
    </ul>
  );
}

export function RequestContextPanel({
  intent,
  isRevision = false,
  className = '',
}: RequestContextPanelProps) {
  const [open, setOpen] = useState(false);

  if (!intent) return null;

  const asks = intent.asks?.filter(Boolean) ?? [];
  const constraints = intent.constraints?.filter(Boolean) ?? [];
  const guardrails = intent.guardrails?.filter(Boolean) ?? [];
  const hasBody =
    Boolean(intent.strategicContext?.trim()) ||
    asks.length > 0 ||
    constraints.length > 0 ||
    guardrails.length > 0 ||
    Boolean(intent.responseGuidance?.trim());

  if (!hasBody) return null;

  return (
    <div
      className={` border border-electric-mustard-200 bg-electric-mustard-50/60 dark:border-electric-mustard-900/50 dark:bg-electric-mustard-950/20 ${className}`}
      data-testid="request-context-panel"
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="flex w-full items-center gap-2 px-4 py-2.5 text-left"
      >
        {open ? (
          <ChevronDown size={14} className="text-electric-mustard-700 dark:text-electric-mustard-400" />
        ) : (
          <ChevronRight size={14} className="text-electric-mustard-700 dark:text-electric-mustard-400" />
        )}
        <Inbox size={14} className="text-electric-mustard-700 dark:text-electric-mustard-400" />
        <span className="text-sm font-semibold text-electric-mustard-900 dark:text-electric-mustard-200">
          {isRevision ? 'What came back with the ball' : 'What was asked of you'}
        </span>
        <span className="ml-auto text-[11px] text-electric-mustard-700 dark:text-electric-mustard-400">
          {open ? 'Hide' : 'Read the request'}
        </span>
      </button>

      {open && (
        <div className="space-y-3 border-t border-electric-mustard-200/70 px-4 py-3 dark:border-electric-mustard-900/50">
          {intent.strategicContext?.trim() && (
            <Section label="Context">
              <p className="whitespace-pre-wrap">{intent.strategicContext}</p>
            </Section>
          )}
          {asks.length > 0 && (
            <Section label={`Asks (${asks.length})`}>
              <BulletList items={asks} />
            </Section>
          )}
          {constraints.length > 0 && (
            <Section label={`Constraints (${constraints.length})`}>
              <BulletList items={constraints} />
            </Section>
          )}
          {guardrails.length > 0 && (
            <Section label={`Guardrails (${guardrails.length})`}>
              <BulletList items={guardrails} />
            </Section>
          )}
          {intent.responseGuidance?.trim() && (
            <Section label="What a good response answers">
              <p className="whitespace-pre-wrap">{intent.responseGuidance}</p>
            </Section>
          )}
          {intent.dueDate && (
            <Section label="Due">
              <p>{new Date(intent.dueDate).toLocaleDateString()}</p>
            </Section>
          )}
        </div>
      )}
    </div>
  );
}

export default RequestContextPanel;
